import { NewCompanyRoleRequest, UpdateCompanyRoleRequest } from "@/types/Settings";
import {
  httpDelete,
  HttpEvent,
  HttpEventCallbacks,
  httpGet,
  httpPost,
  httpPut,
} from "@/events/eventsDomain";
import { RolesStatusResponse } from "@/companyRoles/companyRolesDomain.ts";
import type { RolePeriod, RolePeriodRequest } from "@/companyRoles/rolePeriodsDomain.ts";
import { CompanyRole } from "@/types/CompanyInfo.ts";

export type CompanySettingsApi = {
  updateCompanyRole: (
    orgNo: string,
    data: UpdateCompanyRoleRequest,
    params: HttpEventCallbacks<number>,
  ) => HttpEvent<number>;

  fetchRolesStatus: (
    params: {
      orgNo: string;
    } & HttpEventCallbacks<RolesStatusResponse>,
  ) => HttpEvent<RolesStatusResponse>;

  fetchRolePeriods: (
    params: {
      orgNo: string;
      companyroleId: number;
    } & HttpEventCallbacks<RolePeriod[]>,
  ) => HttpEvent<RolePeriod[]>;

  addRolePeriod: (
    params: {
      orgNo: string;
      companyroleId: number;
      data: RolePeriodRequest;
    } & HttpEventCallbacks<RolePeriod>,
  ) => HttpEvent<RolePeriod>;

  updateRolePeriod: (
    params: {
      orgNo: string;
      companyroleId: number;
      periodId: string;
      data: RolePeriodRequest;
    } & HttpEventCallbacks<RolePeriod>,
  ) => HttpEvent<RolePeriod>;

  removeRolePeriod: (
    params: {
      orgNo: string;
      companyroleId: number;
      periodId: string;
      isLastPeriod: boolean;
    } & HttpEventCallbacks<void>,
  ) => HttpEvent<void>;

  fetchFormerRoles: (
    params: {
      orgNo: string;
    } & HttpEventCallbacks<CompanyRole[]>,
  ) => HttpEvent<CompanyRole[]>;

  createNewRole: (
    params: {
      orgNo: string;
      data: NewCompanyRoleRequest;
    } & HttpEventCallbacks<number>,
  ) => HttpEvent<number>;

  deleteCompanyRole: (
    params: {
      orgNo: string;
      companyroleId: number;
      isResolving: boolean;
    } & HttpEventCallbacks<void>,
  ) => HttpEvent<void>;
};

export const companySettingsApi: CompanySettingsApi = {
  updateCompanyRole: (orgNo, data, params) =>
    httpPost({
      url: `/company-settings/${orgNo}/update-company-role`,
      data,
      ...params,
    }),

  fetchRolesStatus: ({ orgNo, onSuccess, onFailure }) =>
    httpGet({
      url: `/company-settings/${orgNo}/roles-status`,
      onSuccess,
      onFailure,
    }),

  fetchRolePeriods: ({ orgNo, companyroleId, onSuccess, onFailure }) =>
    httpGet({
      url: `/company-settings/${orgNo}/roles/${companyroleId}/periods`,
      onSuccess,
      onFailure,
    }),

  addRolePeriod: ({ orgNo, companyroleId, data, onSuccess, onFailure }) =>
    httpPost({
      url: `/company-settings/${orgNo}/roles/${companyroleId}/periods`,
      data,
      onSuccess,
      onFailure,
    }),

  updateRolePeriod: ({ orgNo, companyroleId, periodId, data, onSuccess, onFailure }) =>
    httpPut({
      url: `/company-settings/${orgNo}/roles/${companyroleId}/periods/${periodId}`,
      data,
      onSuccess,
      onFailure,
    }),

  removeRolePeriod: ({ orgNo, companyroleId, periodId, isLastPeriod, onSuccess, onFailure }) =>
    httpDelete({
      url: `/company-settings/${orgNo}/roles/${companyroleId}/periods/${periodId}?isLastPeriod=${isLastPeriod}`,
      onSuccess,
      onFailure,
    }),

  fetchFormerRoles: ({ orgNo, onSuccess, onFailure }) =>
    httpGet({
      url: `/company-settings/${orgNo}/former-roles`,
      onSuccess,
      onFailure,
    }),

  createNewRole: ({ orgNo, data, onSuccess, onFailure }) =>
    httpPost({
      url: `/company-settings/${orgNo}/new-company-role`,
      data,
      onSuccess,
      onFailure,
    }),

  deleteCompanyRole: ({ orgNo, companyroleId, isResolving, onSuccess, onFailure }) =>
    httpDelete({
      url: `/company-settings/${orgNo}/delete-company-role/${companyroleId}?isResolving=${isResolving}`,
      onSuccess,
      onFailure,
    }),
};
